import { IInsightGraphFilterStatusData } from 'types/ActionTrackerGraphs'
import { highlightInput } from './ActionTrackerTableFunctions'
import { isNotEmpty } from 'utils/data_manipulator'
import { Action } from 'types/BarrierModal'
import { IBarrierActionData } from 'apis/ActionTracker/getBarrierActionTracker'
import { usePermission } from 'hooks/usePermission'
import {
  AVP_SERVICE_ACTION_TRACKER,
  AVP_SERVICE_ASSURANCE_SCORECARD,
  AVP_SERVICE_BARRIER_MANAGEMENT,
  AVP_SERVICE_PROCESS_INTEGRITY,
} from 'constants/avp_permissions'
import { useModulePermission } from 'hooks/useModulePermission'

export const filterActionStatus = (
  status: IInsightGraphFilterStatusData[] | undefined,
  allowedStatuses: string[] = []
): IInsightGraphFilterStatusData[] => {
  if (!status) return []
  const filteredStatus = status?.filter((s) => s.name !== 'Overdue')
  if (!allowedStatuses || allowedStatuses.length === 0) return filteredStatus
  return filteredStatus?.filter((s) => allowedStatuses.includes((s.id as string).toLowerCase()))
}

export const ControlActionDataManipulation = (
  data: Action,
  name: string,
  value: any,
  statusInputRef: React.RefObject<HTMLDivElement>,
  progressPercentageInputRef: React.RefObject<HTMLDivElement>
) => {
  if (name === 'facility_id') {
    data.barriers_ids = [] // Reset barriers when facility changes
  } else if (name === 'barriers_ids') {
    // Reset dependent fields when barriers change
    data.barrier_status = ''
    data.base_action_id = null

    if (!value) {
      data.barriers_ids = []
    }
  } else if (name === 'progress_percent') {
    // Auto-update status based on progress
    if (value == 100 && data?.status != 'COMPLETED') {
      data.status = 'COMPLETED'
      highlightInput(statusInputRef)
    } else if (
      value > 0 &&
      value < 100 &&
      (data?.status == 'OPEN' || data?.status == 'COMPLETED')
    ) {
      data.status = 'IN_PROGRESS'
      highlightInput(statusInputRef)
    }
    if (value < 0 || value > 100) {
      return
    }
  } else if (name === 'status') {
    // Auto-update progress based on status
    if (value === 'COMPLETED') {
      data.progress_percent = '100'
      highlightInput(progressPercentageInputRef)
    } else if (value !== 'COMPLETED' && data?.progress_percent === '100') {
      data.progress_percent = '0'
      highlightInput(progressPercentageInputRef)
    }
  }
  data[name as keyof Omit<Action, 'isNewRecord'>] = value

  if (
    data.status === 'OPEN' &&
    Number(data.progress_percent) !== 0
    // (Number(data.progress_percent) !== 0 || isEmpty(data.progress_percent))
  ) {
    data.progress_percent = '0'
    highlightInput(progressPercentageInputRef)
  }

  if (
    ['IN_PROGRESS', 'COMPLETED'].includes(data.status ?? '') &&
    isNotEmpty(data.progress_percent) &&
    Number(data.progress_percent) === 0
  ) {
    data.status = 'OPEN'
  }

  if (data.status !== 'COMPLETED' && Number(data.progress_percent) !== 100) {
    if (data.comments) data.comments = ''
    if (data.completed_at) data.completed_at = null
  }
}

type UpdateType = 'get' | 'update' | 'create' | 'delete'
type PermissionType = 'list' | 'write' | 'read'

const SERVICE_MAP = {
  metric: AVP_SERVICE_ASSURANCE_SCORECARD,
  barrier: AVP_SERVICE_BARRIER_MANAGEMENT,
  pse: AVP_SERVICE_PROCESS_INTEGRITY,
  actionTracker: AVP_SERVICE_ACTION_TRACKER,
} as const

const ACTION_MAP: Record<
  UpdateType,
  { default: string; observation: string; type: PermissionType }
> = {
  get: {
    default: 'ListAssessmentFormAction',
    observation: 'ListAssessmentFormObservationAction',
    type: 'list',
  },
  update: {
    default: 'CreateUpdateAssessmentFormAction',
    observation: 'CreateUpdateAssessmentFormObservationAction',
    type: 'write',
  },
  create: {
    default: 'CreateUpdateAssessmentFormAction',
    observation: 'CreateUpdateAssessmentFormObservationAction',
    type: 'write',
  },
  delete: {
    default: 'DeleteAssessmentFormAction',
    observation: 'DeleteAssessmentFormObservationAction',
    type: 'write',
  },
}

export const checkActionPermission = (
  action: IBarrierActionData,
  updateType: UpdateType = 'get',
  checkInFacilityPermission: any
): boolean | null => {
  // Determine the type of action
  const isMetricAction = action.barrier_type === null && !!action.metric
  const isBarrierAction = action.barrier_type === 'Barrier'
  const isPSEAction = action.is_pse_barrier

  // Determine service name
  const serviceName = isMetricAction
    ? SERVICE_MAP.metric
    : isPSEAction
      ? SERVICE_MAP.pse
      : isBarrierAction
        ? SERVICE_MAP.barrier
        : SERVICE_MAP.actionTracker

  // Determine permission action and type
  const source = action.action_form_source
  const { default: defaultAction, observation, type } = ACTION_MAP[updateType]
  const permissionAction = source === 'observation' ? observation : defaultAction

  const avpAction = `${serviceName}:${permissionAction}`

  const permission = checkInFacilityPermission({
    action: avpAction,
    type,
    assetId: serviceName === SERVICE_MAP.actionTracker ? undefined : action.asset_id!,
  })

  return permission
}

export const filterPermitedAssets = (
  assets: IInsightGraphFilterStatusData[] | undefined,
  permissions: any,
  actions: string[] = ['ListAssessmentFormObservationAction', 'ListAssessmentFormAction']
) => {
  if (!assets || !permissions?.subprojects) return []

  return assets.filter((asset: any) => {
    const { read = [], write = [], list = [] } = permissions.subprojects[asset.id] ?? {}

    const allPermissions = [...read, ...write, ...list]

    // Must have :GetModuleAccess
    const hasModuleAccess = allPermissions.some((p) => p.endsWith(':GetModuleAccess'))
    if (!hasModuleAccess) return false

    // Then check for at least one of the assessment actions
    return allPermissions.some(
      (p) =>
        actions.some((action) => p.endsWith(`:${action}`))
    )
  })
}

